import {
  GaussianSplatPlugin,
  type GraphicsConfig,
  type Plugin,
} from '@sharefe/share3d-engine'
import {
  isGpuTimingRequested,
  readGraphicsUrlConfig,
  resolveGraphicsBackend,
  toGraphicsConfig,
  type ExampleGraphicsBackend,
} from './graphicsUrlConfig'

export type GsplatBackend = ExampleGraphicsBackend
export type GsplatSortingMode = 'auto' | 'cpu' | 'gpu'
export type GsplatGpuRadixKind = 'auto' | 'portable' | 'onesweep'
export type GsplatPickIdMode = 'direct' | 'buffer'

export interface GsplatBackendControlState {
  readonly backend: GsplatBackend
  readonly sorting: GsplatSortingMode
  readonly radix: GsplatGpuRadixKind
  readonly pickIds: GsplatPickIdMode
}

export interface GsplatBackendControlOptions {
  readonly webgpuSupported?: boolean
}

export function readGsplatBackendControlState(hash = window.location.hash): GsplatBackendControlState {
  const query = new URLSearchParams(hash.split('?')[1] ?? '')
  // backend 走统一解析（兼容历史 renderer= 写法）；sorting/radix/pickIds 是 3DGS 扩展参数。
  const backend = readGraphicsUrlConfig(hash).requestedBackend
  const sortingParam = query.get('sorting')
  const sorting =
    sortingParam === 'cpu' || sortingParam === 'gpu' || sortingParam === 'auto'
      ? sortingParam
      : 'auto'
  const radixParam = query.get('radix')
  const radix =
    radixParam === 'portable' || radixParam === 'onesweep' || radixParam === 'auto'
      ? radixParam
      : 'auto'
  const pickIds = query.get('pickIds') === 'buffer' ? 'buffer' : 'direct'
  return { backend, sorting, radix, pickIds }
}

export function resolveGsplatBackendControlState(
  hash = window.location.hash,
  options: GsplatBackendControlOptions = {}
): GsplatBackendControlState {
  const state = readGsplatBackendControlState(hash)
  if (state.backend === 'webgl2') {
    return {
      ...state,
      sorting: 'cpu',
      radix: 'auto',
    }
  }
  const resolvedBackend = resolveGraphicsBackend(
    { requestedBackend: state.backend, explicit: true },
    { webgpuSupported: options.webgpuSupported }
  )
  if (resolvedBackend === state.backend) {
    return state
  }
  return {
    backend: 'webgl2',
    sorting: 'cpu',
    radix: 'auto',
    pickIds: 'direct',
  }
}

export function getGsplatGraphicsConfig(
  state: GsplatBackendControlState
): GraphicsConfig {
  return toGraphicsConfig(state.backend, { gpuProfiling: isGpuTimingRequested() })
}

type GaussianSplatPluginOptions = ConstructorParameters<typeof GaussianSplatPlugin>[0]

/**
 * example 侧的 3DGS splat 上限。
 *
 * 引擎默认不限制（`maxSplats: 0`），由调用端按业务与设备预算决定。示例要覆盖真实扫描数据
 * （例如 5.5M / 7.7M splats 的 giant/sh0 资产），因此显式设置为 2000 万；该值同时决定 GPU
 * 排序 projection cache 的设备要求（maxSplats × 9 × 4 = 720MB），demo 的预检卡片会如实反映
 * 设备是否满足。
 */
export const EXAMPLE_GSPLAT_MAX_SPLATS = 20_000_000

export function createConfiguredGsplatPlugin(
  state: GsplatBackendControlState,
  options: GaussianSplatPluginOptions = {}
): GaussianSplatPlugin {
  return new GaussianSplatPlugin({
    ...options,
    renderConfig: {
      ...options.renderConfig,
      maxSplats: options.renderConfig?.maxSplats ?? EXAMPLE_GSPLAT_MAX_SPLATS,
      sortingMode: state.backend === 'webgpu' ? state.sorting : 'cpu',
    },
    gpuRadixSortKind:
      state.backend === 'webgpu' && state.sorting !== 'cpu' ? state.radix : undefined,
    gpuPickIdMode: state.pickIds,
  })
}

export function configureGsplatPlugins(
  plugins: readonly Plugin[],
  state: GsplatBackendControlState
): Plugin[] {
  return plugins.map((plugin) => {
    if (plugin.name !== 'gaussian-splat') {
      return plugin
    }
    return createConfiguredGsplatPlugin(state)
  })
}

export function updateGsplatBackendControlState(
  state: GsplatBackendControlState,
  hash = window.location.hash
): void {
  const [path, rawQuery = ''] = hash.split('?')
  const query = new URLSearchParams(rawQuery)
  query.set('backend', state.backend)
  if (state.backend === 'webgpu') {
    query.set('sorting', state.sorting)
    query.set('pickIds', state.pickIds)
    if (state.sorting !== 'cpu') {
      query.set('radix', state.radix)
    } else {
      query.delete('radix')
    }
  } else {
    query.delete('sorting')
    query.delete('radix')
    query.delete('pickIds')
  }
  window.location.hash = `${path}?${query.toString()}`
  window.location.reload()
}
